import _ from "lodash";
import { computed } from "mobx";
import { useMemo } from "react";
import { useFavoriteStore, useLeadStore, useLeaseCompStore, usePipedriveStore, useSaleCompStore, useSavedViewStore, useTenantInMarketStore } from "..";
import IFavorite, { FavoriteTargetType } from "../interfaces/IFavorite";

export default function (searchFilter: string, sortBy: string) {
    const favoriteStore = useFavoriteStore();
    const leadStore = useLeadStore();
    const leaseCompStore = useLeaseCompStore();
    const saleCompStore = useSaleCompStore();
    const pipedriveStore = usePipedriveStore();
    const tenantInMarketStore = useTenantInMarketStore();
    const savedViewStore = useSavedViewStore();

    const favoritesWithSavedViews = useMemo(
        () =>
            computed<IFavorite[]>(() => {
                const savedViewItems: IFavorite[] = savedViewStore.savedViews.map((sv) => ({
                    id: sv.id,
                    targetId: sv.id,
                    targetType: FavoriteTargetType.SavedView,
                    createdAt: sv.modifiedDate,
                }));

                return [...favoriteStore.favorites, ...savedViewItems];
            }),
        [favoriteStore, savedViewStore]
    ).get();

    const sortedFavorites = useMemo(() => computed(() => {
        switch (sortBy) {
            case FavoriteSortingType.AddedNewest:
                return _.orderBy(favoritesWithSavedViews, favorite => favorite.createdAt, "desc");
            case FavoriteSortingType.AddedOldest:
                return _.orderBy(favoritesWithSavedViews, favorite => favorite.createdAt, "asc");
            default:
                return _.orderBy(favoritesWithSavedViews, favorite => favorite.createdAt, "desc");
        }
    }), [favoritesWithSavedViews, sortBy]).get();

    return useMemo(() => {
        if (!searchFilter)
            return sortedFavorites;

        const searchFilterInLowerCase = searchFilter.toLowerCase();

        return sortedFavorites.filter(f => {
            switch (f.targetType) {
                case FavoriteTargetType.Property: {
                    const favoritePropertyDetails = favoriteStore.favoritePropertiesDetails.find(fpd => fpd.id === f.targetId)!;

                    if (!favoritePropertyDetails)
                        return false;

                    return favoritePropertyDetails.address && favoritePropertyDetails.address.toLowerCase().includes(searchFilterInLowerCase);
                }
                case FavoriteTargetType.Deal: {
                    const deal = pipedriveStore.deals.find(d => d.id === f.targetId);

                    if (!deal)
                        return false;

                    return deal.name && deal.name.toLowerCase().includes(searchFilterInLowerCase) ||
                        deal.address && deal.address.toLowerCase().includes(searchFilterInLowerCase);
                }
                case FavoriteTargetType.Lead: {
                    const lead = leadStore.leads.find(l => l.id === f.targetId);

                    if (!lead)
                        return false;

                    return lead.address && lead.address.toLowerCase().includes(searchFilterInLowerCase);
                }
                case FavoriteTargetType.LeaseComp: {
                    const leaseComp = leaseCompStore.leaseComps.find(lc => lc.id === f.targetId);

                    if (!leaseComp)
                        return false;

                    return leaseComp.address && leaseComp.address.toLowerCase().includes(searchFilterInLowerCase);
                }
                case FavoriteTargetType.SaleComp: {
                    const saleComp = saleCompStore.saleComps.find(sc => sc.id === f.targetId);

                    if (!saleComp)
                        return false;

                    return saleComp.address && saleComp.address.toLowerCase().includes(searchFilterInLowerCase);
                }
                case FavoriteTargetType.TenantInMarket: {
                    const tenantInMarket = tenantInMarketStore.tenantsInMarket.find(tim => tim.id === f.targetId);

                    if (!tenantInMarket)
                        return false;

                    return tenantInMarket.description && tenantInMarket.description.toLowerCase().includes(searchFilterInLowerCase);
                }
                case FavoriteTargetType.SavedView: {
                    const savedView = savedViewStore.savedViews.find(sv => sv.id === f.targetId);

                    if (!savedView)
                        return false;

                    return savedView.name && savedView.name.toLowerCase().includes(searchFilterInLowerCase);
                }
                default:
                    return false;
            }
        });
    }, [sortedFavorites, searchFilter]);
}

export class FavoriteSortingType {
    static AddedNewest = "AddedNewest";
    static AddedOldest = "AddedOldest";
}